import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllBrowse } from '../../slices/productSlice';
import ProductFilter from '../../components/ProductFilter';
import ProductList from '../../components/ProductList';
import { ProductGridSkeleton } from '../../components/ProductCardSkeleton';
import { namedColoursForCards, matchesColourFilter } from '../../utils/colourFilter';
import { usePageContent } from '../../hooks/usePageContent';
import styles from './index.module.css';

const BrowseStylesPage = () => {
  const dispatch = useDispatch();
  const { products, loading } = useSelector((state) => state.product);
  const { find } = usePageContent('browse-styles');
  const c = find('styleList') || {};

  const [filterCategory, setFilterCategory] = useState([]);
  const [filterPrice, setFilterPrice] = useState([]);
  const [filterColour, setFilterColour] = useState([]);
  const [filterSize, setFilterSize] = useState([]);
  const [filterVehicleType, setFilterVehicleType] = useState([]);
  const [sortCriteria, setSortCriteria] = useState('popular');
  const [productImages, setProductImages] = useState({});

  useEffect(() => {
    dispatch(fetchAllBrowse());
  }, [dispatch]);

  // Expand each product's browse colour variants into individual flat cards
  const expandToVariantCards = (productList) => {
    const cards = [];
    for (const product of productList) {
      const rawVariants = product.VariantsInBrowseStyle || [];
      const categoryName = product.Category || '';

      const isLucky = product.ProductName?.toLowerCase().includes('lucky');
      const isThemeBased = rawVariants.some(v => v.ThemeInfo?.Theme);
      const isMultiColourTheme = isThemeBased && !isLucky && (() => {
        const themeColours = {};
        for (const v of rawVariants) {
          const theme = v.ThemeInfo?.Theme || '';
          const colour = v.ColourInfo?.DisplayName || v.ColourInfo?.Colour || '';
          if (!themeColours[theme]) themeColours[theme] = new Set();
          if (colour) themeColours[theme].add(colour);
        }
        return Object.values(themeColours).some((colours) => colours.size > 1);
      })();
      const getGroupKey = (v) => {
        if (isLucky || isMultiColourTheme) {
          const colour = v.ColourInfo?.DisplayName || v.ColourInfo?.Colour || '';
          const theme = v.ThemeInfo?.Theme || '';
          return `${colour}_${theme}`;
        }
        return isThemeBased
          ? v.ThemeInfo?.Theme
          : (v.ColourInfo?.DisplayName || v.ColourInfo?.Colour);
      };

      const variants = [...rawVariants].sort((a, b) =>
        (getGroupKey(a) || '').localeCompare(getGroupKey(b) || '')
      );

      const vehicleTypeLabel = product.VehicleType === 1 || product.VehicleType === '1' ? 'Car'
        : product.VehicleType === 2 || product.VehicleType === '2' ? 'Motorcycle'
        : product.VehicleType ? String(product.VehicleType) : '';

      const groups = {};
      const ungroupedCosts = [];
      for (const variant of variants) {
        const key = getGroupKey(variant);
        const ci = variant.CostInfoForCreate;
        const cost = (() => {
          const raw = Array.isArray(ci)
            ? ci.map(e => parseFloat(e?.Cost)).filter(x => !isNaN(x) && x > 0).sort((a,b) => a-b)[0]
            : parseFloat(ci?.Cost);
          return !isNaN(raw) && raw > 0 ? raw : null;
        })();
        if (!key) {
          if (cost != null && cost > 0) ungroupedCosts.push(cost);
          continue;
        }
        if (!groups[key]) {
          groups[key] = { variant, minCost: null, maxCost: null, sizeCodes: [] };
        }
        if (cost != null && cost > 0) {
          if (groups[key].minCost === null || cost < groups[key].minCost) {
            groups[key].minCost = cost;
          }
          if (groups[key].maxCost === null || cost > groups[key].maxCost) {
            groups[key].maxCost = cost;
          }
        }
        const sizeCode = variant.SizeInfo?.SizeCode;
        if (sizeCode && !groups[key].sizeCodes.includes(sizeCode)) {
          groups[key].sizeCodes.push(sizeCode);
        }
      }
      if (ungroupedCosts.length > 0) {
        for (const g of Object.values(groups)) {
          for (const cost of ungroupedCosts) {
            if (g.minCost === null || cost < g.minCost) g.minCost = cost;
            if (g.maxCost === null || cost > g.maxCost) g.maxCost = cost;
          }
        }
      }

      for (const [key, { variant, minCost, maxCost, sizeCodes }] of Object.entries(groups)) {
        const hasST = sizeCodes.includes('ST');
        const hasSL = sizeCodes.includes('SL');
        const allSizeCodes = hasST && hasSL ? [...sizeCodes, 'OOE'] : sizeCodes;
        const hexOuter = variant.ColourInfo?.ColourSelectorHexOuter;
        cards.push({
          ProductId: `${product.ProductId}_${key}`,
          _originalProductId: product.ProductId,
          _variantColour: (isLucky || isMultiColourTheme) ? (variant.ColourInfo?.DisplayName || variant.ColourInfo?.Colour || key) : key,
          _variantTheme: (isLucky || isMultiColourTheme) ? (variant.ThemeInfo?.Theme || null) : (isThemeBased ? key : null),
          _hexOuter: hexOuter ? String(hexOuter).replace(/^#/, '') : '',
          _filterPrimary:   (variant.ColourInfo?.FilterPrimaryColour   || '').toLowerCase(),
          _filterSecondary: (variant.ColourInfo?.FilterSecondaryColour || '').toLowerCase(),
          _sizeCodes: allSizeCodes,
          _showFrom: minCost !== null && maxCost !== null && maxCost > minCost,
          _vehicleType: vehicleTypeLabel,
          ProductName: product.ProductName,
          SampleImageId: product.SampleImageId,
          ListPrice: minCost !== null ? minCost : product.ListPrice,
          _categoryName: categoryName,
          VariantsInBrowseStyle: [variant]
        });
      }

      if (rawVariants.length === 0) {
        cards.push({
          ProductId: product.ProductId,
          _originalProductId: product.ProductId,
          _variantColour: null,
          _hexOuter: '',
          _sizeCodes: [],
          _vehicleType: vehicleTypeLabel,
          ProductName: product.ProductName,
          SampleImageId: product.SampleImageId,
          ListPrice: product.ListPrice,
          _categoryName: categoryName,
          VariantsInBrowseStyle: []
        });
      }
    }

    // Attach _otherColours: sibling colour swatches for the same theme.
    // For variants sharing the same background hex, sort alphabetically —
    // first keeps background hex, others use the "with X" font word.
    const themeHexGroups = {};
    for (const card of cards) {
      if (!card._variantTheme) continue;
      if (!themeHexGroups[card._variantTheme]) themeHexGroups[card._variantTheme] = {};
      const hk = card._hexOuter || '';
      if (!themeHexGroups[card._variantTheme][hk]) themeHexGroups[card._variantTheme][hk] = [];
      themeHexGroups[card._variantTheme][hk].push(card);
    }
    const swatchMap = new Map();
    for (const hexGroups of Object.values(themeHexGroups)) {
      for (const group of Object.values(hexGroups)) {
        const sorted = [...group].sort((a, b) => (a._variantColour || '').localeCompare(b._variantColour || ''));
        sorted.forEach((card, idx) => {
          if (idx === 0) {
            swatchMap.set(card.ProductId, { hex: card._hexOuter || '', swatchColour: '' });
          } else {
            const m = card._variantColour?.match(/with\s+(\w+)/i);
            swatchMap.set(card.ProductId, { hex: '', swatchColour: (m ? m[1] : card._variantColour || '').toLowerCase() });
          }
        });
      }
    }
    const themeColourMap = {};
    for (const card of cards) {
      if (!card._variantTheme) continue;
      if (!themeColourMap[card._variantTheme]) themeColourMap[card._variantTheme] = [];
      const si = swatchMap.get(card.ProductId) || { hex: card._hexOuter || '', swatchColour: '' };
      themeColourMap[card._variantTheme].push({ hex: si.hex, swatchColour: si.swatchColour, colour: card._variantColour });
    }
    for (const card of cards) {
      if (card._variantTheme && themeColourMap[card._variantTheme]) {
        card._otherColours = themeColourMap[card._variantTheme]
          .filter((c) => c.colour !== card._variantColour)
          .slice(0, 5);
      }
    }

    return cards;
  };

  useEffect(() => {
    if (!products || products.length === 0) return;

    const cards = expandToVariantCards(products);
    const imagesMap = {};
    for (const card of cards) {
      const isLucky = card.ProductName?.toLowerCase().includes('lucky');
      const isMotorcycle = card.ProductName?.toLowerCase().includes('motorcycle') && card.ProductName?.toLowerCase().includes('custom');
      let fileName;
      if (isLucky) {
        const safeColour = (card.VariantsInBrowseStyle[0]?.ColourInfo?.DisplayName || card._variantColour || '').replace(/\s+/g, '_');
        const safeTheme = (card._variantTheme || '').replace(/\s+/g, '_');
        fileName = `Lucky_${safeColour}_${safeTheme}.png`;
      } else if (card._variantTheme && card._variantTheme !== card._variantColour) {
        const safeName = card.ProductName.replace(/\s+/g, '_');
        const safeColour = (card._variantColour || '').replace(/\s+/g, '_');
        const safeTheme = (card._variantTheme || '').replace(/\s+/g, '_');
        fileName = `${safeName}_${safeColour}_${safeTheme}.png`;
      } else if (isMotorcycle) {
        const safeColour = (card._variantColour || '').replace(/\s+/g, '_');
        fileName = `Motorcycle_Custom_${safeColour}.png`;
      } else {
        const safeName = card.ProductName.replace(/\s+/g, '_');
        const safeColour = (card._variantColour || '').replace(/\s+/g, '_');
        fileName = safeColour ? `${safeName}_${safeColour}.png` : `${safeName}.png`;
      }
      imagesMap[card.ProductId] = `/sampleimages/${fileName}`;
    }
    setProductImages(imagesMap);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const getProductHref = (product) => {
    const slug = (product.ProductName || '').toLowerCase().replace(/\s+/g, '-');
    return `/browse-styles/${c.slugOverrides?.[product.ProductName] || slug}`;
  };

  const resetFilter = () => {
    setFilterCategory([]);
    setFilterPrice([]);
    setFilterColour([]);
    setFilterSize([]);
    setFilterVehicleType([]);
    setSortCriteria('popular');
  };

  const formatPrice = (price) => `From $${(!price ? '0.00' : Number(price).toFixed(2))}`;

  const allPriceRanges = ['0 - 500', '501 - 1000', '1001 - 9000'];

  const allCategories = [...new Set((products || []).map(p => p.Category).filter(Boolean))].sort();

  const allCards = expandToVariantCards(products || []);

  const allColours = namedColoursForCards(allCards);

  const allSizes = (() => {
    const seen = new Set();
    for (const card of allCards) {
      for (const code of (card._sizeCodes || [])) seen.add(code);
    }
    const order = { OOE: 0, ST: 1, SL: 2 };
    return [...seen].sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99)).map(code => ({ code }));
  })();

  const allVehicleTypes = [...new Set(allCards.map(c => c._vehicleType).filter(Boolean))];

  const inPriceRange = (price) => filterPrice.some(range => {
    if (range === '0 - 500')    return price > 0    && price <= 500;
    if (range === '501 - 1000') return price > 500  && price <= 1000;
    if (range === '1001 - 9000') return price > 1000 && price <= 9000;
    return false;
  });

  // Filter at product level (matching original frontend behaviour):
  // a product passes when ANY of its variant cards satisfies each active filter,
  // then ALL variant cards of that product are included in the results.
  const passingProductIds = new Set();
  const productCardMap = {};
  for (const card of allCards) {
    const pid = String(card._originalProductId);
    if (!productCardMap[pid]) productCardMap[pid] = [];
    productCardMap[pid].push(card);
  }
  for (const [pid, cards] of Object.entries(productCardMap)) {
    const first = cards[0];
    if (filterCategory.length > 0    && !filterCategory.includes(first._categoryName))  continue;
    if (filterVehicleType.length > 0 && !filterVehicleType.includes(first._vehicleType)) continue;
    if (filterPrice.length > 0       && !cards.some(c => inPriceRange(c.ListPrice)))     continue;
    if (filterSize.length > 0        && !cards.some(c => filterSize.some(s => (c._sizeCodes || []).includes(s)))) continue;
    if (filterColour.length > 0      && !cards.some(c => matchesColourFilter(c, filterColour))) continue;
    passingProductIds.add(pid);
  }

  const sortCards = (cards) => [...cards].sort((a, b) => {
    if (sortCriteria === 'price-low') return a.ListPrice - b.ListPrice;
    if (sortCriteria === 'price-high') return b.ListPrice - a.ListPrice;
    if (sortCriteria === 'name-asc') return (a.ProductName || '').localeCompare(b.ProductName || '');
    if (sortCriteria === 'name-desc') return (b.ProductName || '').localeCompare(a.ProductName || '');
    return 0;
  });

  const sortedAvailableProducts = sortCards(allCards.filter(c => passingProductIds.has(String(c._originalProductId))));
  const activeFilterCount = filterVehicleType.length + filterCategory.length + filterPrice.length + filterColour.length + filterSize.length;

  return (
    <div id="content">
      <div className="row">
        <div className="page-header">
          <div className="page-header__content">
            <h1 className="page-header__title">{c.pageTitle || 'Browse Styles'}</h1>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="plate-wizard">
          <div className={`plate-wizard__form plate-wizard__form--style ${styles.formNoBottomPad}`} style={{ paddingTop: 0 }}>
            <form className={`filtered-products${loading ? ' loading' : ''}`} aria-label="Product list">
              {loading ? (
                <div className="products products--ready">
                  <div className="products__inner">
                    <ProductGridSkeleton disclaimers={c.disclaimers} loadingTitle={c.loadingStylesText || 'Loading plate styles...'} showCount />
                  </div>
                </div>
              ) : (
                <>
                  <ProductFilter
                    categories={allCategories}
                    priceRanges={allPriceRanges}
                    colours={allColours}
                    sizes={allSizes}
                    vehicleTypes={allVehicleTypes}
                    filterCategory={filterCategory}
                    filterPrice={filterPrice}
                    filterColour={filterColour}
                    filterSize={filterSize}
                    filterVehicleType={filterVehicleType}
                    showVehicleType
                    sortCriteria={sortCriteria}
                    onFilterChange={(type, value) => {
                      if (type === 'category') setFilterCategory(value);
                      else if (type === 'price') setFilterPrice(value);
                      else if (type === 'colour') setFilterColour(value);
                      else if (type === 'size') setFilterSize(value);
                      else if (type === 'vehicleType') setFilterVehicleType(value);
                    }}
                    filteredCount={activeFilterCount}
                    onSortChange={setSortCriteria}
                    onReset={resetFilter}
                  />

                  <output className="filtered-products__output">
                    <div className="products products--ready">
                      <div className="products__inner">
                        <ProductList
                          products={sortedAvailableProducts}
                          getProductHref={getProductHref}
                          title={c.availableStylesTitle || 'Available styles'}
                          loading={false}
                          formatPrice={formatPrice}
                          productImages={productImages}
                          totalCount={new Set(sortedAvailableProducts.map(c => c._originalProductId)).size}
                        />
                        <div className="styles__disclaimer">
                          {(c.disclaimers || [
                            'Plate images are a guide only. There may be small variations to colour and spacing.',
                            'Representations of plates provided on this page are for illustration purposes only, final products may vary slightly from what is displayed on-screen.',
                          ]).map((text, i) => <p key={i}>{text}</p>)}
                        </div>
                      </div>
                    </div>
                  </output>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowseStylesPage;
